"use client";

import type { CSSProperties } from "react";
import type { ProjectClip, ProjectTrack, OverdubMonitorSource, PlaybackMode } from "../lib/multitrack-model";

type Props = {
  tracks: ProjectTrack[];
  clips: ProjectClip[];
  ppq: number;
  roleLabels: Record<ProjectTrack["role"], string>;
  roleColors: Record<ProjectTrack["role"], string>;
  selectedTrackId: string | null;
  selectedClipId: string | null;
  isPlaying: boolean;
  isExporting: boolean;
  onSelectTrack: (trackId: string) => void;
  onSelectClip: (clipId: string) => void;
  onChangeTrack: (trackId: string, patch: Partial<ProjectTrack>) => void;
  onPlayTrack: (track: ProjectTrack) => void;
  onExportTrack: (track: ProjectTrack) => void;
};

const PLAYBACK_MODES: PlaybackMode[] = ["audio", "midi", "both"];
const MONITOR_SOURCES: OverdubMonitorSource[] = ["off", "audio", "midi", "both"];

export function MultiTrackArrange(props: Props) {
  const clipsById = new Map(props.clips.map((clip) => [clip.id, clip]));
  const timelineTicks = Math.max(
    props.ppq * 16,
    ...props.clips.map((clip) => clip.startTick + clip.durationTicks),
  );
  const selectedTrack = props.tracks.find((track) => track.id === props.selectedTrackId) ?? null;
  const selectedClip = props.clips.find((clip) => clip.id === props.selectedClipId) ?? null;

  return (
    <div className="multitrack-arrange" aria-label="TrackとClipを分離したマルチトラックarrange">
      <header className="arrange-ruler"><span>TRACK</span><div>{[1, 2, 3, 4].map((bar) => <i key={bar}>{bar}</i>)}</div></header>
      {props.tracks.map((track, index) => {
        const trackClips = track.clipIds.flatMap((clipId) => clipsById.get(clipId) ?? []);
        return (
          <article
            key={track.id}
            className={`arrange-track${props.selectedTrackId === track.id ? " selected" : ""}${track.muted ? " muted" : ""}`}
            style={{ "--track-color": props.roleColors[track.role] } as CSSProperties}
          >
            <div className="arrange-track-head">
              <button className="arrange-track-identity" onClick={() => props.onSelectTrack(track.id)}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{track.name}</strong>
                <small>{props.roleLabels[track.role]}</small>
              </button>
              <div className="arrange-track-switches">
                <button className={track.muted ? "active" : ""} onClick={() => props.onChangeTrack(track.id, { muted: !track.muted })}>M</button>
                <button className={track.solo ? "active" : ""} onClick={() => props.onChangeTrack(track.id, { solo: !track.solo })}>S</button>
              </div>
            </div>
            <div className="arrange-track-routing">
              <label><span>PLAY</span><select aria-label={`${track.name} playback mode`} value={track.playbackMode} onChange={(event) => props.onChangeTrack(track.id, { playbackMode: event.target.value as PlaybackMode })}>{PLAYBACK_MODES.map((mode) => <option key={mode}>{mode}</option>)}</select></label>
              <label><span>REC MON</span><select aria-label={`${track.name} overdub monitor source`} value={track.overdubMonitorSource} onChange={(event) => props.onChangeTrack(track.id, { overdubMonitorSource: event.target.value as OverdubMonitorSource })}>{MONITOR_SOURCES.map((source) => <option key={source}>{source}</option>)}</select></label>
            </div>
            <div className="arrange-clip-lane">
              {trackClips.map((clip) => {
                const left = clip.startTick / timelineTicks * 100;
                const width = Math.max(4, clip.durationTicks / timelineTicks * 100);
                return <button
                  key={clip.id}
                  className={`arrange-clip${props.selectedClipId === clip.id ? " selected" : ""}`}
                  style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
                  onClick={(event) => { event.stopPropagation(); props.onSelectTrack(track.id); props.onSelectClip(clip.id); }}
                ><strong>{clip.name}</strong><small>{clip.midi?.notes.length ?? 0} MIDI · {clip.audio ? "AUDIO" : "NO AUDIO"}</small></button>;
              })}
            </div>
            <div className="arrange-track-actions">
              <button onClick={() => props.onPlayTrack(track)} disabled={props.isPlaying}>聴く</button>
              <button onClick={() => props.onExportTrack(track)} disabled={props.isExporting}>MIDI</button>
            </div>
          </article>
        );
      })}
      {(selectedTrack || selectedClip) && (
        <footer className="arrange-context" aria-label="TrackとClipの独立選択">
          <div><span>TRACK SELECTED</span><strong>{selectedTrack?.name ?? "—"}</strong></div>
          <div><span>CLIP SELECTED</span><strong>{selectedClip?.name ?? "—"}</strong></div>
          <nav aria-label="将来のTrackとClip操作">
            <button disabled title="Phase 4で既存Piano Rollへ接続">EDIT</button>
            <button disabled>RENAME</button>
            <button disabled>RE-RECORD</button>
            <button disabled>SPLIT</button>
            <button disabled>MOVE</button>
            <button disabled>DUPLICATE</button>
          </nav>
        </footer>
      )}
    </div>
  );
}
